"use client";

import { useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/card";
// import {
//   JsonView,
//   collapseAllNested,
//   defaultStyles,
// } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";
import { useSelectedCamera } from "@/context/selected-camera-context";
import { useCameraContext } from "@/context/camera-context";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Camera as CameraIcon,
  ChevronDown,
  Copy,
  Eye,
  EyeOff,
} from "lucide-react";
import { CameraActions } from "./camera-actions";
import { CameraConnectivityDetails, CameraStatus } from "./camera-status";
import { CameraControl } from "./control/camera-control";
import { useLocalization } from "@/context/localization-context";

// const StyledJsonView = styled(JsonView)`
//   padding: 1rem;
//   border-radius: 0.5rem;
//   background-color: hsl(var(--muted));
//   border: 1px solid hsl(var(--border));
//   max-height: 600px;
//   overflow-y: auto;
// `;

const CameraDetail = () => {
  const { t } = useLocalization();
  const { selectedDeviceId } = useSelectedCamera();
  const { cameras } = useCameraContext();
  const [showToken, setShowToken] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const selectedCamera = useMemo(() => {
    if (!selectedDeviceId || !cameras) return null;
    return cameras.find((camera) => camera.id === selectedDeviceId) || null;
  }, [selectedDeviceId, cameras]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (!selectedCamera) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CameraIcon className="h-5 w-5" />
            {t("cameras.detail.title")}
          </CardTitle>
          <CardDescription>
            {t("cameras.detail.selectHint")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            {t("cameras.detail.noneSelected")}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CollapsibleTrigger asChild>
                <div className="cursor-pointer select-none">
                  <CardTitle className="flex items-center gap-2">
                    <CameraIcon className="h-5 w-5" />
                    {selectedCamera.name}
                    <ChevronDown
                      className={`h-4 w-4 text-muted-foreground transition-transform ${
                        detailsOpen ? "rotate-180" : ""
                      }`}
                    />
                  </CardTitle>
                  <CardDescription>
                    {t("cameras.detail.deviceIdValue", { id: selectedCamera.id })}
                  </CardDescription>
                </div>
              </CollapsibleTrigger>
              <div className="flex items-center gap-2">
                <CameraStatus connectivity={selectedCamera.connectivity} />
                <Badge variant={selectedCamera.assigned ? "default" : "secondary"}>
                  {selectedCamera.assigned
                    ? t("cameras.assignment.assigned")
                    : t("cameras.assignment.unassigned")}
                </Badge>
                <CameraActions camera={selectedCamera} />
              </div>
            </div>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="space-y-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t("cameras.columns.deviceId")}</label>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm">{selectedCamera.id}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(selectedCamera.id)}
                  className="h-6 w-6 p-0"
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t("common.name")}</label>
              <div className="flex items-center gap-2">
                <span>{selectedCamera.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(selectedCamera.name)}
                  className="h-6 w-6 p-0"
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t("cameras.columns.branchUuid")}</label>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm">
                  {selectedCamera.branch_uuid || "-"}
                </span>
                {selectedCamera.branch_uuid && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(selectedCamera.branch_uuid!)}
                    className="h-6 w-6 p-0"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Connectivity */}
          <CameraConnectivityDetails connectivity={selectedCamera.connectivity} />

          {/* Token */}
          <div>
            <label className="text-sm font-medium text-muted-foreground">{t("cameras.columns.token")}</label>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm">
                {showToken ? selectedCamera.token : "••••••••••••••••"}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowToken(!showToken)}
                className="h-6 w-6 p-0"
              >
                {showToken ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(selectedCamera.token)}
                className="h-6 w-6 p-0"
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Camera control: live photo, ROI focus, exposure/WB/focus/stream */}
      <CameraControl deviceId={selectedCamera.id} />

      {/* Raw Data */}
      {/* <Card>
        <CardHeader>
          <CardTitle>Raw Data</CardTitle>
          <CardDescription>
            Complete camera object in JSON format
          </CardDescription>
        </CardHeader>
        <CardContent>
          <StyledJsonView
            data={selectedCamera}
            shouldExpandNode={collapseAllNested}
            style={defaultStyles}
          />
        </CardContent>
      </Card> */}
    </div>
  );
};

export default CameraDetail;